import axios from "axios";
import { API_URL } from "./config";

const adminApi = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// ───────────────────────────────────────
// Response interceptor — auto-refresh on 401
// ───────────────────────────────────────
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  failedQueue = [];
};

adminApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      !error.response ||
      error.response.status !== 401 ||
      originalRequest._retry ||
      originalRequest.url?.includes("/admin/refresh") ||
      originalRequest.url?.includes("/admin/login")
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(() => {
        return adminApi(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      await axios.post(`${API_URL}/api/admin/refresh`, {}, { withCredentials: true });
      processQueue(null);
      return adminApi(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError);
      localStorage.removeItem("admin_username");
      localStorage.removeItem("admin_name");
      window.dispatchEvent(new Event("admin:auth:expired"));
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// ───────────────────────────────────────
// Admin Auth
// ───────────────────────────────────────
export const adminLogin = (username, password) =>
  adminApi.post("/admin/login", { username, password });

// Admin Entities
export const getAdminEntities = () => adminApi.get("/admin/entities");
export const getAdminEntityDetail = (entityId) => adminApi.get(`/admin/entities/${entityId}`);
export const updateAdminEntity = (entityId, data) => adminApi.put(`/admin/entities/${entityId}`, data);

// Admin Consultes
export const getAdminConsultes = () => adminApi.get("/admin/consultes");
export const getAdminConsultaDetail = (consultaId) => adminApi.get(`/admin/consultes/${consultaId}`);
export const adminUpdateConsulta = (consultaId, data) => adminApi.put(`/admin/consultes/${consultaId}`, data);
export const adminActivateConsulta = (consultaId) => adminApi.put(`/admin/consultes/${consultaId}/activate`);
export const adminRejectConsulta = (consultaId) => adminApi.put(`/admin/consultes/${consultaId}/reject`);
export const adminCloseConsulta = (consultaId) => adminApi.put(`/admin/consultes/${consultaId}/close`);
export const adminRejectClosure = (consultaId) => adminApi.put(`/admin/consultes/${consultaId}/reject-closure`);

// Admin Stats
export const getAdminStats = () => adminApi.get("/admin/stats");
export const getAdminDashboardStats = () => adminApi.get("/admin/stats");

// Admin Results
export const getAdminResults = (consultaId) => adminApi.get(`/admin/results/${consultaId}`);
export const downloadAdminPdf = (consultaId) => adminApi.get(`/admin/results/${consultaId}/pdf`, { responseType: 'blob' });

// Admin Deletion
export const requestDeletion = (itemType, itemId) =>
  adminApi.post("/admin/request-deletion", { item_type: itemType, item_id: itemId });

// Admin Scrutiny Requests
export const getAdminScrutinyRequests = (status) =>
  adminApi.get("/admin/scrutiny-requests", { params: status ? { status } : {} });

// Admin Key Regeneration
export const adminApproveKeyRegen = (entityId) => adminApi.put(`/admin/entities/${entityId}/approve-key-regeneration`);
export const adminRejectKeyRegen = (entityId) => adminApi.put(`/admin/entities/${entityId}/reject-key-regeneration`);

// Admin Users
export const getAdminUsers = () => adminApi.get("/admin/users");
export const createAdminUser = (data) => adminApi.post("/admin/users", data);
export const updateAdminUser = (userId, data) => adminApi.put(`/admin/users/${userId}`, data);
export const deleteAdminUser = (userId) => adminApi.delete(`/admin/users/${userId}`);

export const adminSimulateVotes = (consultaId, numVotes) =>
  adminApi.post(`/admin/simulate-votes/${consultaId}`, { num_votes: numVotes });

export default adminApi;
